import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchInvoices } from '../../api/invoicesApi';
import { fetchProfile } from '../../api/profile';
import SpeakerInvitationPopup from '../Dashboard/SpeakerInvitationPopup';
import '../styles/layout/sidebar.css';

const Sidebar = ({ isOpen, onClose, onPageChange, onGroupTicketOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const [speakerMenuShow, setSpeakerMenuShow] = useState(false);
  const [showInvitationPopup, setShowInvitationPopup] = useState(false);
  const [invitationEventId, setInvitationEventId] = useState(null);

  const [badges, setBadges] = useState({
    submissions: null,
    purchases: null,
    invoices: null,
  });
  const [badgesLoading, setBadgesLoading] = useState(true);

  const eventLogo = 'https://emiratesscholar.com/system/images/logo-esrc.png';
  const eventName = 'Emirates Scholar Center';

  useEffect(() => {
    const updateSidebarUser = () => {
      const storedProfile = localStorage.getItem("user_profile");
      const storedDetails = localStorage.getItem("user_details");

      if (storedProfile) {
        try {
          const profileData = JSON.parse(storedProfile);
          const detailsData = storedDetails ? JSON.parse(storedDetails) : {};

          const avatarUrl = detailsData?.imageLink || profileData?.usr_det?.imageLink || "";
          const titleVal = detailsData?.title || profileData?.usr_det?.title || '';
          const cleanedTitle = titleVal ? titleVal.replace(/\.$/, '') : '';
          const isValidTitle = ['mr', 'mrs', 'ms', 'dr', 'prof'].includes(cleanedTitle.toLowerCase());
          const formattedTitle = isValidTitle ? cleanedTitle.charAt(0).toUpperCase() + cleanedTitle.slice(1).toLowerCase() + '.' : '';

          const name = profileData?.name || '';
          const lname = profileData?.lname || '';
          const membership = detailsData?.membership || profileData?.usr_det?.membership || '';
          const createdYear = profileData?.created_at ? new Date(profileData.created_at).getFullYear() : new Date().getFullYear();

          setUser({
            title: formattedTitle,
            name: name,
            lname: lname,
            displayName: `${formattedTitle ? formattedTitle + ' ' : ''}${name} ${lname}`.replace(/\s+/g, ' ').trim(),
            esid: membership ? `ESID ${membership} ${createdYear}` : `ESID ${createdYear}`,
            avatar: avatarUrl,
          });
        } catch (error) {
          console.error("❌ [Sidebar] Error parsing user data:", error);
        }
      }
    };

    // Load local storage data immediately
    updateSidebarUser();

    // Fetch live profile from the server to refresh sidebar dynamically
    const loadProfileData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        await fetchProfile();
        updateSidebarUser();
      } catch (e) {
        console.error("❌ [Sidebar] Failed to fetch live profile:", e);
      }
    };
    loadProfileData();
  }, []);

  // ✅ التحقق من Speaker Status
  const checkSpeakerStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setSpeakerMenuShow(false);
        return;
      }

      console.log('📡 [Sidebar] Checking active speaker management list...');
      const response = await fetch(
        'https://emiratesscholar.com/system/api/active-speaker-managment-list',
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('✅ [Sidebar] Active Speaker Management List:', data);

        if (data && data.speakerAcceptRequest === true) {
          const extractedEventId = data.eventID || data.eventId || data.event_id || null;
          setInvitationEventId(extractedEventId);
          setShowInvitationPopup(true);
        }

        if (data && data.speakerMenuShow === true) {
          setSpeakerMenuShow(true);
          return;
        }

        let hasConferences = false;
        if (Array.isArray(data) && data.length > 0) {
          hasConferences = true;
        } else if (data && typeof data === 'object') {
          const list = data.mySpeakerManagement || data.data || data.conferences || data.list || [];
          if (Array.isArray(list) && list.length > 0) {
            hasConferences = true;
          }
        }

        setSpeakerMenuShow(hasConferences);
        return;
      }

      // Fallback: check speaker with current event_context eventId
      const eventContext = JSON.parse(localStorage.getItem('event_context') || '{}');
      const eventId = eventContext?.eventId || eventContext?.chairId;

      if (eventId) {
        console.log('📡 [Sidebar] Fallback check speaker for event:', eventId);
        const response = await fetch(
          `https://emiratesscholar.com/system/api/check-speaker?event_id=${eventId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data && data.speakerAcceptRequest === true) {
            setInvitationEventId(eventId);
            setShowInvitationPopup(true);
          }
          if (data.speakerMenuShow === true && data.speakerAcceptRequest !== false) {
            setSpeakerMenuShow(true);
            return;
          }
        }
      }

      setSpeakerMenuShow(false);
    } catch (error) {
      console.error('❌ [Sidebar] Error:', error);
      setSpeakerMenuShow(false);
    }
  }, []);

  useEffect(() => {
    checkSpeakerStatus();
  }, [checkSpeakerStatus]);

  const fetchSubmissionCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 0;
      const response = await fetch('https://emiratesscholar.com/system/api/portal-user-all-submissions', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (!response.ok) return 0;
      const text = await response.text();
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) return 0;
      const json = JSON.parse(text);
      // ✅ Use the correct total: data["0"].total is the full count of all submissions
      // "Total Submissions" field is also available directly on the response
      return json?.['Total Submissions'] ?? json?.['0']?.total ?? (json?.['0']?.data || []).length ?? 0;
    } catch (error) { return 0; }
  };

  const fetchPurchasesCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 0;
      const response = await fetch('https://emiratesscholar.com/system/api/active-purchases', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (!response.ok) return 0;
      const text = await response.text();
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) return 0;
      return (JSON.parse(text)?.tickets || []).length;
    } catch (error) { return 0; }
  };

  const fetchInvoicesCount = async () => {
    try {
      const { stats } = await fetchInvoices();
      return stats.total;
    } catch (error) { return 0; }
  };

  useEffect(() => {
    const loadBadges = async () => {
      setBadgesLoading(true);
      const [submissionsCount, purchasesCount, invoicesCount] = await Promise.all([
        fetchSubmissionCount(),
        fetchPurchasesCount(),
        fetchInvoicesCount(),
      ]);
      setBadges({ submissions: submissionsCount, purchases: purchasesCount, invoices: invoicesCount });
      setBadgesLoading(false);
    };
    loadBadges();
  }, []);

  const getInitials = () => {
    if (!user) return '?';
    return (user.name?.charAt(0) || '') + (user.lname?.charAt(0) || '') || '?';
  };

  const getValidAvatarUrl = () => {
    if (!user?.avatar || avatarError) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    if (user.avatar.startsWith('/')) return `https://citescholar.com${user.avatar}`;
    return `https://citescholar.com/portal/public/users/${user.avatar}`;
  };

  const menuItems = [
    {
      section: null,
      items: [
        { icon: 'dashboard', label: t('dashboard', 'Dashboard'), path: '/dashboard', pageTitle: 'dashboard' }
      ]
    },
    // ✅ Speaker Section - يظهر فقط إذا speakerMenuShow = true
    ...(speakerMenuShow ? [
      {
        section: t('speaker_section', 'Speaker Management'),
        items: [
          { icon: 'person', label: t('speaker_management', 'Speaker Management'), path: '/speakermanagement', pageTitle: 'speaker_management' }
        ]
      }
    ] : []),
    {
      section: t('manage', 'Manage'),
      items: [
        { icon: 'featured_play_list', label: t('manage_submissions', 'Submission'), path: '/submissions', badge: badges.submissions, pageTitle: 'manage_submissions' },
        { icon: 'credit_card', label: t('active_purchases', 'Conference Tickets'), path: '/purchases', badge: badges.purchases, pageTitle: 'active_purchases' },
        { icon: 'design_services', label: t('author_services', 'Author services'), path: '/author-services', pageTitle: 'author_services' },
        { icon: 'mail', label: t('letters_certificates', 'Letters & Certificates'), path: '/letters', pageTitle: 'letters_certificates' },
        { icon: 'list_alt', label: t('invoices_payments', 'Invoices & Payments'), path: '/invoices', badge: badges.invoices, pageTitle: 'invoices_payments' },
      ]
    },
    {
      section: t('socials', 'Socials'),
      items: [
        { icon: 'forum', label: t('social_wall', 'Social wall'), path: '/social-wall', pageTitle: 'social_wall' },
        { icon: 'account_tree', label: t('my_network', 'My network'), path: '/network', pageTitle: 'my_network' }
      ]
    },
    {
      section: t('profile', 'Profile'),
      items: [
        { icon: 'account_circle', label: t('my_profile', 'My profile'), path: '/profile', pageTitle: 'my_profile' },
        { icon: 'support', label: t('support', 'Support'), path: '/support', pageTitle: 'support' },
        { icon: 'password', label: t('reset_password', 'Reset password'), path: '/ResetPassword', pageTitle: 'reset_password' },
        { isHeader: true, label: t('important_links', 'Important links') },
        { icon: 'link', label: t('official_website', 'Office website'), path: 'https://emiratesscholar.com', external: true, pageTitle: 'official_website' },
        { icon: 'list', label: t('publications', 'Publications'), path: '/Publications', pageTitle: 'publications' },
        { icon: 'delete', label: t('delete_account', 'Delete Account'), path: '/DeleteAccount', danger: true, pageTitle: 'delete_account' }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavClick = (e, item) => {
    e.preventDefault();
    // Action items (no path) — e.g. Group Ticket popup
    if (item.action === 'group_ticket') {
      if (onGroupTicketOpen) onGroupTicketOpen();
      onClose();
      return;
    }
    if (onPageChange && item.pageTitle) onPageChange(item.pageTitle);
    if (item.external) window.open(item.path, '_blank', 'noopener,noreferrer');
    else navigate(item.path);
    onClose();
  };

  const avatarUrl = getValidAvatarUrl();
  const initials = getInitials();
  const fullName = user?.displayName || 'Dr. Firas Habbal';

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Escape' && onClose()} />
      <aside className={`esc-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-logo-wrap">
          <div className="esc-logo-sidebar">
            <img alt={eventName || "ESC Logo"} src={eventLogo} />
          </div>
        </div>
        <div className="sidebar-user-wrap">
          {avatarUrl ? (
            <img src={avatarUrl} alt={fullName} className="sb-avatar-img" onError={() => setAvatarError(true)} />
          ) : (
            <div className="sb-avatar-fallback">{initials}</div>
          )}
          <div className="sb-user-info">
            <div className="sb-user-name">{fullName}</div>
            <div className="sb-user-id">{user?.esid || 'ESID-0000 0000 2023'}</div>
          </div>
          <div className="sb-online-dot" title={t('online')} />
        </div>
        <div className="esc-sidebar-inner">
          {menuItems.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {section.section && <div className="sb-sec">{section.section}</div>}
              <div className="sb-items-group">
                {section.items.map((item, itemIndex) => {
                  if (item.isHeader) {
                    return (
                      <div key={itemIndex} className="sb-sec-sub" style={{ padding: '12px 16px 4px', fontSize: '11px', fontWeight: '700', color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: '0.5px solid rgba(0,0,0,0.06)', marginTop: '8px', textAlign: 'start' }}>
                        {item.label}
                      </div>
                    );
                  }
                  return (
                    <a key={itemIndex} href={item.external ? item.path : undefined} target={item.external ? '_blank' : undefined} rel={item.external ? 'noopener noreferrer' : undefined} className={`ni ${isActive(item.path) ? 'active' : ''} ${item.danger ? 'danger' : ''}`} style={{ marginTop: "1px" }} onClick={(e) => handleNavClick(e, item)}>
                      <span className="ni-icon"><i className="material-icons">{item.icon}</i></span>
                      <span className="ni-label">{item.label}</span>
                      {item.badge !== null && item.badge !== undefined && item.badge > 0 && <span className="nb">{item.badge}</span>}
                      {badgesLoading && item.badge === undefined && item.badge !== null && <span className="nb nb-loading">...</span>}
                    </a>
                  );
                })}
              </div>
              {sectionIndex < menuItems.length - 1 && <div className="sb-divider" />}
            </React.Fragment>
          ))}

          {/* Group Ticket Booking Banner Card under items */}
          <div style={{ padding: '16px', margin: '16px 16px 12px', borderRadius: '12px', background: 'linear-gradient(135deg, #1d1e20ff, #03172bff)', color: '#fff', position: 'relative', overflow: 'hidden', textAlign: 'start' }}>
            <span className="material-icons" style={{ position: 'absolute', right: '-10px', bottom: '-15px', fontSize: '90px', opacity: 0.1, pointerEvents: 'none' }}>confirmation_number</span>
            <h4 style={{ fontSize: '13px', fontWeight: '700', margin: '0 0 6px 0', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('group_ticket_title', 'Group Ticket Bookings')}</h4>
            <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.82)', margin: '0 0 12px 0', lineHeight: '1.4' }}>
              {t('group_ticket_desc', 'Group ticket bookings are handled directly by our team. Contact us to check availability.')}
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                if (onGroupTicketOpen) onGroupTicketOpen();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#fff',
                color: '#185FA5',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <i className="material-icons" style={{ fontSize: '14px' }}>group</i>
              {t('request_group_ticket', 'Request Group Ticket')}
            </button>
          </div>
        </div>
        <div className="sb-footer">{t('version')}</div>
      </aside>

      {/* Speaker Invitation Popup */}
      <SpeakerInvitationPopup
        isOpen={showInvitationPopup}
        onClose={() => setShowInvitationPopup(false)}
        eventId={invitationEventId}
        onStatusChange={() => {
          checkSpeakerStatus();
        }}
      />
    </>
  );
};

export default Sidebar;